import {createSlice, Update} from '@reduxjs/toolkit';
import {Notification} from '../entities';
import {NotificationEntities, NotificationPreferencesByCategory, NotificationsState} from './types';
import {notificationsAdapter} from './adapters';
import {
  deleteNotification,
  deregisterPushNotifications,
  loadNotificationCategories,
  loadNotificationPreferences,
  loadNotifications,
  markNotificationsAsRead,
  markNotificationsCustomActionAsPerformed,
  registerPushNotifications,
  setInAppBadgeNumber,
  setInstallation,
  updateNotificationPreference,
  updateNotificationPreferences,
} from './actions';

const initialState: NotificationsState = {
  installation: {
    deviceId: '',
    platform: '',
    token: '',
    tags: [],
    version: 0,
  },
  preferences: {
    Updates: {
      UserEmail: false,
      PushNotification: false,
    },
    ShiftReminder: {
      UserEmail: false,
      PushNotification: false,
    },
    Connections: {
      UserEmail: false,
      PushNotification: false,
    },
    GroupBookingInvitations: {
      UserEmail: false,
      PushNotification: false,
    },
    ReservationReminder: {
      UserEmail: false,
      PushNotification: false,
    },
  } as NotificationPreferencesByCategory,
  preferencesLoadingStatus: 'NotLoaded',
  isUpdatingPreferences: false,
  allNotifications: <NotificationEntities>notificationsAdapter.getInitialState(),
  allNotificationsLoadingStatus: 'NotLoaded',
  canDisplayEmptyNotifications: false,
  inAppBadgeNumber: 0,
  isRegisteringNotifications: false,
  isDeregisteringNotifications: false,
  categoryData: {
    categories: [],
    status: 'NotLoaded',
  },
};

const slice = createSlice({
  name: 'notification',
  initialState: initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(setInAppBadgeNumber, (state, action) => {
        state.inAppBadgeNumber = action.payload;
      })
      .addCase(setInstallation, (state, action) => {
        state.installation = action.payload;
      })
      .addCase(registerPushNotifications.pending, (state) => {
        state.isRegisteringNotifications = true;
      })
      .addCase(registerPushNotifications.fulfilled, (state, action) => {
        state.installation = action.payload;
        state.isRegisteringNotifications = false;
      })
      .addCase(registerPushNotifications.rejected, (state) => {
        state.isRegisteringNotifications = false;
      })
      .addCase(deregisterPushNotifications.pending, (state) => {
        state.isDeregisteringNotifications = true;
      })
      .addCase(deregisterPushNotifications.fulfilled, (state) => {
        state.isDeregisteringNotifications = false;
      })
      .addCase(deregisterPushNotifications.rejected, (state) => {
        state.isDeregisteringNotifications = false;
      })
      .addCase(loadNotificationPreferences.pending, (state) => {
        state.preferencesLoadingStatus = 'Loading';
      })
      .addCase(loadNotificationPreferences.fulfilled, (state, action) => {
        const preferences = action.payload;

        const preferencesByCategory = preferences.reduce((accumulator, current) => {
          accumulator[current.category] = {
            ...accumulator[current.category],
            [current.type]: current.enabled,
          };
          return accumulator;
        }, {} as NotificationPreferencesByCategory);

        state.preferences = preferencesByCategory;
        state.preferencesLoadingStatus = 'Loaded';
      })
      .addCase(loadNotificationPreferences.rejected, (state) => {
        state.preferencesLoadingStatus = 'Failed';
      })

      .addCase(updateNotificationPreference.pending, (state, action) => {
        state.isUpdatingPreferences = true;
        const {category, type, enabled} = action.meta.arg;
        const categoryPreference = state.preferences[category];
        if (categoryPreference) {
          categoryPreference[type] = enabled; // Consider it as updated, so we avoid UI glitches (switch)
        }
      })
      .addCase(updateNotificationPreference.fulfilled, (state, action) => {
        const {category, type, enabled} = action.payload;
        const categoryPreference = state.preferences[category];
        if (categoryPreference) {
          categoryPreference[type] = enabled;
        }
        state.isUpdatingPreferences = false;
      })
      .addCase(updateNotificationPreference.rejected, (state, action) => {
        state.isUpdatingPreferences = false;
        const {category, type, enabled} = action.meta.arg;
        const categoryPreference = state.preferences[category];
        if (categoryPreference) {
          categoryPreference[type] = !enabled; // Rollback to the original value if it fails
        }
      })

      .addCase(updateNotificationPreferences.pending, (state, action) => {
        state.isUpdatingPreferences = true;
        const preferences = action.meta.arg;

        // Consider it as updated, so we avoid UI glitches (switch)
        preferences.forEach(({category, type, enabled}) => (state.preferences[category][type] = enabled));
      })
      .addCase(updateNotificationPreferences.fulfilled, (state, action) => {
        state.isUpdatingPreferences = false;
        const preferences = action.meta.arg;

        preferences.forEach(({category, type, enabled}) => (state.preferences[category][type] = enabled));
      })
      .addCase(updateNotificationPreferences.rejected, (state, action) => {
        state.isUpdatingPreferences = false;
        const preferences = action.meta.arg;

        // Rollback to the original value if it fails
        preferences.forEach(({category, type, enabled}) => (state.preferences[category][type] = !enabled));
      })

      .addCase(loadNotifications.pending, (state, action) => {
        state.allNotificationsLoadingStatus = 'Loading';
      })
      .addCase(loadNotifications.fulfilled, (state, action) => {
        const notifications = action.payload;
        notificationsAdapter.setAll(state.allNotifications, notifications ?? []);
        state.canDisplayEmptyNotifications = notifications.length === 0;
        state.allNotificationsLoadingStatus = 'Loaded';
      })
      .addCase(loadNotifications.rejected, (state) => {
        state.allNotificationsLoadingStatus = 'Failed';
      })

      .addCase(loadNotificationCategories.pending, (state, action) => {
        state.categoryData.status = 'Loading';
      })
      .addCase(loadNotificationCategories.fulfilled, (state, action) => {
        const categories = action.payload;
        state.categoryData.categories = categories;
        state.categoryData.status = 'Loaded';
      })
      .addCase(loadNotificationCategories.rejected, (state) => {
        state.categoryData.status = 'Failed';
      })

      .addCase(markNotificationsAsRead.fulfilled, (state, action) => {
        const updates: Update<Notification>[] = action.payload.map(({id, isRead}) => ({
          id,
          changes: {isRead},
        }));
        notificationsAdapter.updateMany(state.allNotifications, updates);
      })
      .addCase(markNotificationsCustomActionAsPerformed.fulfilled, (state, action) => {
        state.allNotifications.entities[action.meta.arg].isCustomActionPerformed = true;
      })
      .addCase(deleteNotification.pending, (state, action) => {
        const notificationId = action.meta.arg.id;
        state.allNotifications.entities[notificationId].isDeleting = true;
      })
      .addCase(deleteNotification.fulfilled, (state, action) => {
        const notification = action.payload;
        notificationsAdapter.removeOne(state.allNotifications, notification.id);
      })
      .addCase(deleteNotification.rejected, (state, action) => {
        const notificationId = action.meta.arg.id;
        state.allNotifications.entities[notificationId].isDeleting = false;
      });
  },
});

export const {reducer: notificationsReducer} = slice;
